/**
 * De casos reales a lo que enseña la pestaña de cámara.
 *
 * Antes esto lo fabricaba `buildDashcamState` a partir de un hash del id de la
 * unidad: tres indicadores en verde y hasta siete eventos con hora y gravedad
 * inventadas. Una unidad sin cámara reportando nada se veía igual que una
 * vigilada, y los mismos «eventos» salían cada vez porque el hash no cambia.
 *
 * Aquí no se inventa: un evento de cámara es un caso que Centinela abrió con
 * uno de los tipos que nacen de la cámara. Si no hay ninguno, el panel lo dice.
 */
import type {
  CentinelaBitacoraEntry,
  CentinelaEvent,
} from "../../../features/centinela/types";
import type { CameraEvent, CameraEventSummary } from "../types";

/** Los tipos del catálogo que nacen de la cámara (nt-28…nt-32). */
export const CAMERA_KINDS = ["nt-28", "nt-29", "nt-30", "nt-31", "nt-32"] as const;

const esDeCamara = (kind: string): boolean =>
  (CAMERA_KINDS as readonly string[]).includes(kind);

/**
 * La etiqueta sale del catálogo servido, no de la pantalla: el caso viaja con
 * el id del tipo (`nt-28`) y nada más, así que sin catálogo se muestra el id
 * crudo antes que una etiqueta adivinada que envejece con el catálogo.
 */
export function toCameraEvents(
  events: CentinelaEvent[],
  labels: Record<string, string>,
): CameraEvent[] {
  return events
    .filter((item) => esDeCamara(item.case.kind))
    .map((item) => ({
      id: item.case.id,
      kind: item.case.kind,
      at: item.case.opened_at,
      label: labels[item.case.kind] ?? item.case.kind,
      urgency: item.case.urgency,
      origen: "caso" as const,
    }))
    .sort((a, b) => b.at.localeCompare(a.at));
}

/**
 * Los tipos de cámara que NUNCA abren caso.
 *
 * nt-31 y nt-32 son `solo_registro`: el pipeline los anota en la bitácora y
 * ahí termina. Sin leer esa segunda fuente, la conducta al volante y toda la
 * asistencia a la conducción —salida de carril, colisión frontal, distancia de
 * seguimiento— quedarían invisibles en la pantalla por más que la cámara las
 * reporte.
 */
export const CAMERA_LOG_KINDS = ["nt-31", "nt-32"] as const;

/** La entrada de bitácora ya trae su etiqueta: no hace falta el catálogo. */
export function toCameraLogEvents(entradas: CentinelaBitacoraEntry[]): CameraEvent[] {
  return entradas
    .filter((e) => e.at && (CAMERA_LOG_KINDS as readonly string[]).includes(e.alert_type_id ?? ""))
    .map((e) => ({
      id: e.id ?? `${e.alert_type_id}-${e.at}`,
      kind: e.alert_type_id ?? "",
      at: e.at as string,
      label: e.label ?? e.alert_type_id ?? "",
      // Son de solo registro por definición: no despiertan a nadie.
      urgency: "baja",
      origen: "bitacora" as const,
    }))
    .sort((a, b) => b.at.localeCompare(a.at));
}

/**
 * Une las dos fuentes en una sola lista, de la más reciente a la más vieja.
 *
 * Se descarta el duplicado igual que en el parte: el mismo tipo dentro del
 * mismo minuto es el mismo hecho, y el caso gana sobre la anotación porque
 * lleva más información detrás.
 */
export function mergeCameraEvents(
  deCasos: CameraEvent[],
  deBitacora: CameraEvent[],
): CameraEvent[] {
  const vistos = new Set(
    deCasos.map((e) => `${e.kind}:${Math.floor(Date.parse(e.at) / 60000)}`),
  );

  const sinRepetir = deBitacora.filter((e) => {
    const llave = `${e.kind}:${Math.floor(Date.parse(e.at) / 60000)}`;
    if (vistos.has(llave)) return false;
    vistos.add(llave);
    return true;
  });

  return [...deCasos, ...sinRepetir].sort((a, b) => b.at.localeCompare(a.at));
}

/**
 * Los tipos de evento que la cámara SÍ reportó, con su conteo.
 *
 * Antes aquí había tres casillas fijas —somnolencia, distracción, vista al
 * frente— que se apoyaban en nt-28/29/30. Esos son tipos de cabina y ninguno
 * está mapeado todavía, así que las tres decían «sin datos» siempre, hubiera o
 * no actividad. Esto enseña lo que llegó de verdad y desaparece cuando no hay
 * nada, en vez de fingir tres huecos.
 */
export function resumenPorTipo(cameraEvents: CameraEvent[]): CameraEventSummary[] {
  const porTipo = new Map<string, CameraEventSummary>();

  for (const evento of cameraEvents) {
    const previo = porTipo.get(evento.kind);
    if (!previo) {
      porTipo.set(evento.kind, {
        kind: evento.kind,
        label: evento.label,
        total: 1,
        ultimo: evento.at,
      });
      continue;
    }
    previo.total += 1;
    // `cameraEvents` viene ordenado del más reciente al más viejo, pero no se
    // da por hecho: compararlo cuesta nada y sobrevive a un reordenamiento.
    if (evento.at.localeCompare(previo.ultimo) > 0) previo.ultimo = evento.at;
  }

  return [...porTipo.values()].sort(
    (a, b) => b.total - a.total || b.ultimo.localeCompare(a.ultimo),
  );
}

/** Cuántos de esos eventos cayeron hoy, para el contador del panel. */
export function countCameraEventsToday(
  cameraEvents: CameraEvent[],
  now: Date = new Date(),
): number {
  const hoy = now.toDateString();

  return cameraEvents.filter((event) => {
    const at = new Date(event.at);
    return !Number.isNaN(at.getTime()) && at.toDateString() === hoy;
  }).length;
}
